import { useLanguage } from "../../../stores/LanguageStore";
import { Button, Group, Indicator, Tooltip } from "@mantine/core";
import { IconAdjustmentsHorizontal, IconX } from "@tabler/icons-react";
import FilterContent from "../../../components/Filter/FilterContent";
import { memo, useCallback, useMemo } from "react";
import {
  GRID_CHECKBOX_SELECTION_FIELD,
  GRID_TREE_DATA_GROUPING_FIELD,
  gridColumnDefinitionsSelector,
  gridFilterModelSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid-pro";
import PopOver from "../../../components/View/PopOver";

function getItems(v, inGroup) {
  if (v?.values && v?.mode) {
    return {
      mode: v?.mode,
      values: v.values.map((_v) => getItems(_v, true)),
      id: v?.id,
    };
  }
  if (inGroup) {
    return {
      field_name: v?.field,
      type: v?.operator?.toLowerCase(),
      value: v?.value?.toString(),
      id: v?.id,
    };
  }
  return {
    field_name: v?.field,
    type: v?.operator?.toLowerCase(),
    value: v?.value?.toString(),
    id: v?.id,
  };
}

const TableFilter = memo(
  ({ tableId, pageName, isClient, additionalFilter = [], whiteButtons }) => {
    const apiRef = useGridApiContext();
    const lang = useLanguage((s) => s.language);

    const currentFilter = useGridSelector(apiRef, gridFilterModelSelector);
    const mainFilter = useMemo(() => {
      if (currentFilter)
        return {
          mode: currentFilter?.logicOperator?.toLowerCase() || "or",
          values: currentFilter?.items.map((item) => getItems(item)) || [],
        };
      return null;
    }, [currentFilter]);

    const columns = useGridSelector(
      apiRef,
      gridColumnDefinitionsSelector
    ).filter(
      (x) =>
        ![
          GRID_CHECKBOX_SELECTION_FIELD,
          GRID_TREE_DATA_GROUPING_FIELD,
          "actions",
        ].includes(x.field)
    );

    const handleFilterChange = useCallback(
      (newFilter) => {        
        const quickFilter =
          apiRef.current.state.filter.filterModel.quickFilterValues?.join(" ");

        function getItems(v) {
          if (v?.values && v?.mode) {
            return {
              mode: v?.mode,
              values: v.values.map((_v) => getItems(_v)),
              id: v?.id,
            };
          }
          return {
            field: v.field_name,
            value: v.value,
            operator: v.type,
            id: v?.id,
          };
        }

        let newTableFilter = {
          items: newFilter?.values?.map((val) => getItems(val)) || [],
          logicOperator: newFilter?.mode || "and",
          quickFilterValues: quickFilter?.split(" ") || [],
        };

        apiRef.current.setFilterModel(newTableFilter);
      },
      [apiRef]
    );

    const fields = useMemo(() => {
      return [
        {
          label: lang.contact_lists.to_blacklist.columns,
          options: columns
            .filter((x) => x.filterable)
            .map((column) => ({
              id: column?.field,
              name: column?.headerName,
              option_name: column?.headerName,
              type: column?.type || "text",
              options: column?.getFilterOptions?.(),
              ...column,
            })),
        },
        {
          label: lang.new_ui_onboarding.other,
          options: additionalFilter.map((column) => ({
            id: column?.field,
            name: column?.headerName,
            option_name: column?.headerName,
            type: column?.type || "text",
            options: column?.getFilterOptions?.(),
            ...column,
          })),
        },
      ];
    }, [additionalFilter, columns]);

    return (
      <Group gap={0} mt={2} ml={16} mr={whiteButtons ? 0 : 16}>
        <PopOver
          opener={(openFunction, ref) => {
            return (
              <Group gap={0}>
                {whiteButtons ? (
                  <Tooltip
                    label={lang.table.toolbar.filter.main_button_tooltip}
                  >
                    <Button
                      ref={ref}
                      onClick={openFunction}
                      radius={"md"}
                      variant={"subtle"}
                      className="test"
                      style={{
                        borderColor: "var(--mantine-color-primary-0)",
                        background: "#fff",
                        borderTopRightRadius:
                          currentFilter?.items?.length > 0 ? 0 : 8,
                        borderBottomRightRadius:
                          currentFilter?.items?.length > 0 ? 0 : 8,
                      }}
                      id={"table-filter-" + tableId}
                    >
                      <Indicator
                        color="red"
                        offset={2}
                        disabled={currentFilter?.items?.length === 0}
                        label={currentFilter?.items?.length}
                        styles={{
                          indicator: {
                            padding: "7px 4px",
                          },
                        }}
                        size={16}
                      >
                        <IconAdjustmentsHorizontal size={20} />
                      </Indicator>
                      <span style={{ marginLeft: 6 }}>
                        {lang.table.toolbar.filter.main_button_label}
                      </span>
                    </Button>
                  </Tooltip>
                ) : (
                  <Tooltip
                    label={lang.table.toolbar.filter.main_button_tooltip}
                  >
                    <Indicator
                      color="red"
                      offset={2}
                      disabled={currentFilter?.items?.length === 0}
                      label={currentFilter?.items?.length}
                      styles={{
                        indicator: {
                          fontSize: 9,
                          padding: "6px",
                        },
                      }}
                    >
                      <Button
                        ref={ref}
                        onClick={openFunction}
                        variant={
                          currentFilter?.items?.length > 0 ? "light" : "subtle"
                        }
                        size={"sm"}
                        id={"table-filter-" + tableId}
                        miw={0}
                        w={38}
                        p={0}
                        radius={"md"}
                        style={{
                          borderTopRightRadius:
                            currentFilter?.items?.length > 0 ? 0 : 12,
                          borderBottomRightRadius:
                            currentFilter?.items?.length > 0 ? 0 : 12,
                        }}
                      >
                        <IconAdjustmentsHorizontal size={24} />
                      </Button>
                    </Indicator>
                  </Tooltip>
                )}
                {currentFilter?.items?.length > 0 && (
                  <Tooltip
                    label={lang.table.toolbar.filter.cancel_button_tooltip}
                  >
                    <Button
                      variant="light"
                      color="red"
                      px={4}
                      radius={"md"}
                      style={{
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                        borderColor: "var(--mantine-color-primary-0)",
                        borderLeft: 0,
                      }}
                      onClick={() => handleFilterChange(null)}
                    >
                      <IconX size={18} />
                    </Button>
                  </Tooltip>
                )}
              </Group>
            );
          }}
          popoverChildren={(closePopover) => (
            <FilterContent
              id={tableId}
              isClient={isClient}
              fields={fields}
              mainFilter={mainFilter}
              setMainFilter={(v) => {
                handleFilterChange(v);
                closePopover();
              }}
              getFieldOptionName={(f) => f.name}
              pageName={pageName}
            />
          )}
          paperSx={{
            ml: -12,
            borderRadius: 3,
          }}
        />
      </Group>
    );
  }
);

TableFilter.displayName = "TableFilter";

export default TableFilter;
